import axios from "axios";
import router from "@/router";
import { clearAuthStatus } from "@/assets/Js/auth.js";

// =====================================================
// Axios API 實例
// -----------------------------------------------------
// HttpOnly Cookie 架構重點：
// 1. 前端不讀 token。
// 2. 前端不手動加 Authorization: Bearer。
// 3. 瀏覽器會因 withCredentials: true 自動帶 Cookie。
// 4. 後端從 Cookie 驗證 JWT。
// =====================================================
const apiClient = axios.create({
  //baseURL: "http://localhost:7036/api", // 本機後端 API 根路徑
  baseURL: "https://orderfood-api.christylove.com.tw/api", // 正式後端 API 根路徑
  withCredentials: true,
});

// 控制 401 工作階段失效只執行一次導頁。
// MainConsole 掛載時會同時發出多支 API；若全部一起 401，不能讓每支請求各自跳一次登入頁。
let isRedirectingToLogin = false;
const SESSION_EXPIRED_NOTICE_KEY = "orderfoodpos.sessionExpiredNotice";

// Cookies-only 架構：不附加 Authorization 標頭。
apiClient.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error),
);

// 後台 API 若回 401，代表 Cookie 無效、過期或尚未登入。
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const statusCode = error.response?.status;
    const requestUrl = error.config?.url || "";

    // 打卡 API 使用員工帳密驗證，不代表目前 POS Cookie 失效。
    // 即使舊版後端或代理誤回 401，也不可清除主系統登入狀態。
    const skipSessionLogout = [
      "/auth/status",
      "/login",
      "/personnel/login",
    ].some((endpoint) => requestUrl.startsWith(endpoint));

    if (statusCode === 401 && !skipSessionLogout) {
      clearAuthStatus();

      if (!isRedirectingToLogin) {
        isRedirectingToLogin = true;

        const loginPath = "/maincole/login";
        const currentRoute = router.currentRoute.value;
        const redirectPath =
          currentRoute.path === loginPath
            ? currentRoute.query.redirect || "/mainconsole"
            : currentRoute.fullPath || "/mainconsole";

        try {
          // 登入頁載入後再顯示提示，避免 MainConsole 同時多支 401 的 SweetAlert 互相覆蓋。
          sessionStorage.setItem(SESSION_EXPIRED_NOTICE_KEY, "1");
        } catch (storageError) {
          console.warn("無法保存登入逾時提示狀態", storageError);
        }

        // 401 屬於整個商家工作階段失效，使用 replace + 整頁導向可立即中止
        // MainConsole 尚未完成的 API/catch，避免它們在登入頁上繼續跳「載入失敗」。
        const loginUrl = router.resolve({
          path: loginPath,
          query: { redirect: String(redirectPath) },
        }).href;

        window.location.replace(loginUrl);
      }

      // 頁面即將離開，不把這筆 401 繼續丟回原頁面的 catch 顯示第二層錯誤視窗。
      return new Promise(() => {});
    }

    return Promise.reject(error);
  },
);

export default {
  // 控制 GET 請求：同時相容 api.get(url, query) 與 Axios 的 api.get(url, { params })。
  get(endpoint, paramsOrConfig = {}) {
    const isAxiosConfig =
      paramsOrConfig &&
      typeof paramsOrConfig === "object" &&
      ["params", "headers", "responseType", "timeout", "signal"].some((key) =>
        Object.prototype.hasOwnProperty.call(paramsOrConfig, key),
      );

    return isAxiosConfig
      ? apiClient.get(endpoint, paramsOrConfig)
      : apiClient.get(endpoint, { params: paramsOrConfig });
  },

  post(endpoint, data, config = {}) {
    return apiClient.post(endpoint, data, config);
  },

  put(endpoint, data, config = {}) {
    return apiClient.put(endpoint, data, config);
  },

  patch(endpoint, data, config = {}) {
    return apiClient.patch(endpoint, data, config);
  },

  // 控制 DELETE 請求：相容直接傳 body 與 Axios 的 { data } 設定格式。
  delete(endpoint, dataOrConfig = {}) {
    const isAxiosConfig =
      dataOrConfig &&
      typeof dataOrConfig === "object" &&
      ["data", "headers", "timeout", "signal"].some((key) =>
        Object.prototype.hasOwnProperty.call(dataOrConfig, key),
      );

    return isAxiosConfig
      ? apiClient.delete(endpoint, dataOrConfig)
      : apiClient.delete(endpoint, { data: dataOrConfig });
  },
};
